<template>
  <ts-modal
    ref="modal"
    :modal-class="computedModalClass"
    :modal-dialog-class="computedModalDialogClass"
    :modal-title="computedModalTitle"
    :keyboard="allowKeyboard"
    @modal-show="onModalShow"
    @modal-shown="onModalShown"
    @modal-hidden="onModalHidden"
  >
    <template #modal-content>
      <div
        v-if="error"
        class="alert alert-danger"
        role="alert"
      >
        {{ error }}
      </div>
      <loading-indicator
        v-if="isLoading && !loadingIndicatorInFooter"
        :message="loadingMessage"
      />
      <slot
        name="modal-content"
        :modal-state="modalState"
      >
        <div
          v-if="modalForm && formHtml != null"
          ref="formWrapper"
          v-html="formHtml"
        />
      </slot>
    </template>
    <template #modal-footer>
      <slot
        name="modal-footer"
        :modal-state="modalState"
      >
        <button
          v-if="deleteUrl && modalForm && formHtml != null"
          type="button"
          class="btn btn-sm btn-danger me-auto"
          @click="showDeleteConfirmation"
        >
          {{ deleteTitle || 'Delete' }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-secondary"
          data-bs-dismiss="modal"
        >
          Close
        </button>
        <button
          v-if="modalForm && formHtml != null && submitButton"
          class="btn btn-sm btn-primary"
          :disabled="submittingForm"
          @click.prevent="submitForm"
        >
          {{ submitTitle }}
        </button>
      </slot>
    </template>
    <template #modal-footer-after>
      <loading-indicator
        v-if="isLoading && loadingIndicatorInFooter"
        :message="loadingMessage"
      />
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, useTemplateRef} from 'vue'
import Rails from "@rails/ujs"
import axios, {type AxiosRequestConfig} from 'axios'
import LoadingIndicator from './loading-indicator.vue'
import { isNonEmptyString } from '@/js/js-utils'
import { initializeFormFields } from '../site-utils'
import TsModal from "@/js/vue-components/ts-modal.vue";

interface ModalState {
  initialDataLoaded: boolean;
  isLoading: boolean;
}

export interface DeleteConfirmation {
  url: string,
  title: string,
  message: string,
}

export interface ModalOptions {
  modalDialogClass?: string
}

const props = defineProps({
  modalTitle: {default: '', type: String},
  modalClass: {default: null, type: String},
  modalDialogClass: {default: null, type: String},
  loadingMessage: {default: null, type: String},
  loadingIndicatorInFooter: {default: true, type: Boolean},
  hideContentWhenLoading: {default: false, type: Boolean},
  allowKeyboard: {default: false, type: Boolean},
  modalForm: {default: false, type: Boolean},
})

const modal = useTemplateRef("modal")
const formWrapper = useTemplateRef("formWrapper")

const closeUrl = ref<string | null>(null)
const customModalTitle = ref<string | null>(null)
const customSubmitTitle = ref<string | null>(null)
const deleteConfirmationTitle = ref<string | null>(null)
const deleteConfirmationMessage = ref<string | null>(null)
const deleteTitle = ref<string | null>(null)
const deleteUrl = ref<string | null>(null)
const error = ref<string | null>(null)
const formHtml = ref<string | null>(null)
const initialLoadPerformed = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const loadParams = ref<any>(null)
const loadUrl = ref<string | null>(null)
const requestConfig = ref<AxiosRequestConfig | undefined>(undefined)
const submitButton = ref<boolean>(true)
const submittingForm = ref<boolean>(false)
const modalOptions = ref<ModalOptions>({})

const modalState = computed((): ModalState => {
  return {
    initialDataLoaded: initialLoadPerformed.value,
    isLoading: isLoading.value,
  }
})

const computedModalTitle = computed((): string => {
  if (isNonEmptyString(customModalTitle.value)) {
    return customModalTitle.value
  }

  return props.modalTitle
})

const submitTitle = computed((): string => {
  if (isNonEmptyString(customSubmitTitle.value)) {
    return customSubmitTitle.value
  }

  return 'Save'
})

const computedModalClass = computed((): string => {
  const classes = ['modal-form']
  if (props.modalClass) {
    classes.push(props.modalClass)
  }
  return classes.join(' ')
})

const computedModalDialogClass = computed((): string => {
  const classes = [props.modalDialogClass]
  if (modalOptions.value.modalDialogClass) {
    classes.push(modalOptions.value.modalDialogClass)
  }
  return classes.join(' ')
})

const showLoadingIndicator = (show: boolean): void => {
  isLoading.value = show
}

const setError = (anError: string | null): void => {
  error.value = anError
}

const showModal = (aLoadUrl: string, params?: any, aRequestConfig?: AxiosRequestConfig, aModalOptions?: ModalOptions): void => {
  if (!modal.value || modal.value.isOpen) {
    return
  }

  modalOptions.value = aModalOptions || {}
  setError(null)
  isLoading.value = false

  loadUrl.value = aLoadUrl
  loadParams.value = params
  requestConfig.value = aRequestConfig
  initialLoadPerformed.value = false
  formHtml.value = null

  modal.value?.show()
}

const hideModal = (): void => {
  modal.value?.hide()
}

const onModalHidden = () => {
  emit('modal-hidden', closeUrl.value)
}

const onModalShow = () => {
  loadDetails()
}

const onModalShown = () => {
  emit('modal-shown')
}

const loadDetails = (): void => {
  error.value = null

  const theLoadUrl = loadUrl.value
  if (isNonEmptyString(theLoadUrl)) {
    showLoadingIndicator(true)
    const reqConfig = requestConfig.value || {} as AxiosRequestConfig
    if (props.modalForm) {
      reqConfig['headers'] = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Accept': 'application/json',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-CSRF-Token': Rails.csrfToken(),
      }
    }

    const formRequestConfig: AxiosRequestConfig = Object.assign({method: "GET", url: theLoadUrl}, reqConfig)
    if (loadParams.value) {
      if (formRequestConfig.method == "POST" || formRequestConfig.method == "post") {
        formRequestConfig.data = loadParams.value
      } else {
        formRequestConfig.params = loadParams.value
      }
    }

    axios.request(formRequestConfig)
      .then((response) => {
        showLoadingIndicator(false)
        initialLoadPerformed.value = true

        setupForm(response.data)
        emit('details-loaded', response.data)
      })
      .catch((e) => {
        error.value = e.message
        showLoadingIndicator(false)
        initialLoadPerformed.value = true
        emit('details-load-error', e.message)
      })
  }
}

const emit = defineEmits<{
  (e: 'modal-shown'): void
  (e: 'modal-hidden', closeUrl: string | null): void
  (e: 'details-loaded', data: any): void
  (e: 'details-load-error', error: string): void
  (e: 'form-success'): void
  (e: 'delete-confirmation', confirmation: DeleteConfirmation): void
}>()

defineExpose({
  hideModal,
  setError,
  showLoadingIndicator,
  showModal,
  get modalEl(): HTMLDivElement | null {
    return modal.value?.modalEl || null
  }
})

const submitForm = (): void => {
  const aFormWrapper = formWrapper.value
  if (aFormWrapper) {
    const formEl = aFormWrapper.querySelector(':scope form')
    if (formEl instanceof HTMLFormElement) {
      Rails.fire(formEl, 'submit')
    }
  }
}

const setupForm = (responseData: any): void => {
  if (props.modalForm) {
    formHtml.value = isNonEmptyString(responseData?.html) ? responseData.html : null
    customModalTitle.value = isNonEmptyString(responseData?.modal_title) ? responseData.modal_title : null
    customSubmitTitle.value = isNonEmptyString(responseData?.submit_title) ? responseData.submit_title : null
    deleteUrl.value = responseData?.delete?.url || null
    deleteTitle.value = responseData?.delete?.title || null
    deleteConfirmationTitle.value = responseData?.delete?.confirmationTitle || null
    deleteConfirmationMessage.value = responseData?.delete?.confirmationMessage || null
    submitButton.value = responseData?.submit_button !== false
    closeUrl.value = isNonEmptyString(responseData?.modal_close_url) ? responseData.modal_close_url : null

    nextTick(() => {
      initForm()
    })
  }
}

const initForm = (): void => {
  const aFormWrapper = formWrapper.value
  if (aFormWrapper) {
    const formEl = aFormWrapper.querySelector(':scope form')
    if (formEl instanceof HTMLFormElement && formEl.getAttribute('data-form-initialized') !== 'true') {
      formEl.addEventListener('submit', (Rails as any).handleRemote)

      // need to set initialized attribute, so attachment to events
      // does not happen multiple times if formHTML does not changes
      // between the requests
      formEl.setAttribute('data-form-initialized', 'true')

      // force request to be json
      formEl.setAttribute('data-type', 'json')

      // initialize form
      initializeFormFields(formEl)

      // attach to events
      formEl.addEventListener('ajax:beforeSend', () => {
        showLoadingIndicator(true)
        submittingForm.value = true
        return true
      })
      formEl.addEventListener('ajax:success', (e) => {
        submittingForm.value = false
        showLoadingIndicator(false)

        const [data] = (e as any).detail
        if (data.status == 'success') {
          // form operation succeeded
          emit('form-success')
          return
        }
        setupForm(data)
      })
      formEl.addEventListener('ajax:error', (e) => {
        submittingForm.value = false

        const [, status, xhr] = (e as any).detail
        alert(`Error: ${xhr.status} - ${status}`)
        showLoadingIndicator(false)
      })
    }
  }
}

const showDeleteConfirmation = (): void => {
  if (deleteUrl.value === null) {
    return
  }

  const confirmation: DeleteConfirmation = {
    url: deleteUrl.value,
    title: deleteConfirmationTitle.value || 'Delete record?',
    message: deleteConfirmationMessage.value || 'Are you sure?'
  }

  emit('delete-confirmation', confirmation)
}

</script>
